import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, resolveUrl } from "@/lib/api";
import { BrandCard } from "@/components/BrandCard";

function GroupLogo({ settings }) {
  if (settings?.group_logo_url) {
    return (
      <img
        src={resolveUrl(settings.group_logo_url)}
        alt="The CASS Group"
        className="h-9 w-auto object-contain mix-blend-multiply"
        data-testid="group-logo"
      />
    );
  }
  return (
    <span className="font-heading text-xl font-semibold tracking-tight text-stone-900" data-testid="group-logo">
      The CASS Group
    </span>
  );
}

export default function Home() {
  const [brands, setBrands] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/brands"), api.get("/settings")])
      .then(([b, s]) => {
        setBrands(b.data);
        setSettings(s.data);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (settings) {
      document.title = settings.seo_title || "The CASS Group";
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", settings.seo_description || "");
      if (settings.primary_color) {
        document.documentElement.style.setProperty("--cass-primary", settings.primary_color);
      }
      if (settings.favicon_url) {
        const href = resolveUrl(settings.favicon_url);
        let link = document.querySelector("link[rel='icon']");
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.head.appendChild(link);
        }
        link.href = href;
        let apple = document.querySelector("link[rel='apple-touch-icon']");
        if (!apple) {
          apple = document.createElement("link");
          apple.rel = "apple-touch-icon";
          document.head.appendChild(apple);
        }
        apple.href = href;
      }
    }
  }, [settings]);

  const primary = settings?.primary_color || "#292524";
  const count = brands.length;
  const containerMax =
    count <= 1 ? "max-w-md" : count === 2 ? "max-w-3xl" : "max-w-6xl";
  const gridColsClass =
    count <= 1
      ? "md:grid-cols-1"
      : count === 2
      ? "md:grid-cols-2"
      : count === 3
      ? "md:grid-cols-2 lg:grid-cols-3"
      : "md:grid-cols-2 lg:grid-cols-4";
  const accentColors = brands
    .map((b) => b.accent_color)
    .filter((c) => /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(c || ""));
  const lineGradient =
    accentColors.length >= 2
      ? `linear-gradient(90deg, ${accentColors.join(", ")})`
      : "linear-gradient(90deg, #2EA3FD, #007A6B, #FE7202, #0B12AF)";

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-stone-200/70 bg-stone-50/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center" data-testid="header-home-link">
            <GroupLogo settings={settings} />
          </Link>
          <nav className="flex items-center gap-8 text-sm font-medium text-stone-600">
            <a href="#top" className="hidden transition-colors hover:text-stone-900 sm:inline" data-testid="nav-home">
              Home
            </a>
            <a href="#brands" className="transition-colors hover:text-stone-900" data-testid="nav-brands">
              Our Brands
            </a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative">
        <div className={`mx-auto ${containerMax} px-6 pb-10 pt-10 text-center md:pb-14 md:pt-14`}>
          {settings?.group_logo_url && (
            <motion.img
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              src={resolveUrl(settings.group_logo_url)}
              alt="The CASS Group"
              className="mx-auto mb-5 h-20 w-auto object-contain mix-blend-multiply md:h-24"
              data-testid="hero-group-logo"
            />
          )}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-stone-900 sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            {settings?.headline || "Specialist businesses. One connected group."}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-lg leading-relaxed text-stone-600"
            data-testid="hero-subheadline"
          >
            {settings?.subheadline}
          </motion.p>
          {settings?.intro_paragraph && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-4 text-base leading-relaxed text-stone-500"
              data-testid="hero-intro"
            >
              {settings.intro_paragraph}
            </motion.p>
          )}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-10"
          >
            <a
              href="#brands"
              className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-white shadow-sm transition-transform duration-300 hover:scale-[1.03] active:scale-95"
              style={{ backgroundColor: primary }}
              data-testid="hero-cta"
            >
              Explore our brands
            </a>
          </motion.div>
        </div>
      </section>

      {/* Brands */}
      <section id="brands" className="mx-auto max-w-6xl px-6 pb-28 pt-4">
        <div className="mb-12 flex flex-col items-start gap-3">
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-stone-900" data-testid="brands-heading">
            Our Brands
          </h2>
          <p className="max-w-xl text-base leading-relaxed text-stone-600">
            Each business within the group specialises in its own area, sharing a common focus on practical support and technology.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-80 animate-pulse rounded-2xl border border-stone-200 bg-white" />
            ))}
          </div>
        ) : count === 0 ? (
          <p className="text-stone-500" data-testid="brands-empty">No brands are currently available.</p>
        ) : (
          <div className={`mx-auto grid grid-cols-1 gap-6 lg:gap-8 ${containerMax} ${gridColsClass}`} data-testid="brands-grid">
            {brands.map((brand, i) => (
              <BrandCard key={brand.id} brand={brand} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="on-dark bg-[#17233C] text-white">
        <div className="mx-auto max-w-6xl px-6 py-14">
          <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="font-heading text-xl font-semibold tracking-tight text-white">
                {settings?.footer_text || "The CASS Group"}
              </p>
              <p className="mt-1 text-sm italic text-white/70" data-testid="footer-tagline">
                {settings?.footer_tagline || "Giving You More Than Numbers"}
              </p>
              {(settings?.footer_address || settings?.footer_email || settings?.footer_phone) && (
                <div className="mt-5 space-y-1.5 text-sm not-italic text-white/70">
                  {settings?.footer_address && (
                    <p className="whitespace-pre-line" data-testid="footer-address">{settings.footer_address}</p>
                  )}
                  {settings?.footer_email && (
                    <a href={`mailto:${settings.footer_email}`} className="block transition-colors hover:text-white" data-testid="footer-email">
                      {settings.footer_email}
                    </a>
                  )}
                  {settings?.footer_phone && (
                    <a href={`tel:${settings.footer_phone.replace(/\s+/g, "")}`} className="block transition-colors hover:text-white" data-testid="footer-phone">
                      {settings.footer_phone}
                    </a>
                  )}
                </div>
              )}
            </div>
            {brands.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/50">Group businesses</p>
                {brands.map((b) =>
                  b.website_url ? (
                    <a
                      key={b.id}
                      href={b.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-white/80 transition-colors hover:text-white"
                    >
                      {b.brand_name}
                    </a>
                  ) : (
                    <span key={b.id} className="text-sm text-white/50">{b.brand_name}</span>
                  )
                )}
              </div>
            )}
          </div>
          <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-6 text-sm text-white/60 sm:flex-row sm:items-center sm:justify-between">
            <p>© {new Date().getFullYear()} The CASS Group. All rights reserved.</p>
            <div className="flex gap-6">
              <span className="cursor-default transition-colors hover:text-white">Privacy Policy</span>
              <span className="cursor-default transition-colors hover:text-white">Cookie Policy</span>
              <Link to="/admin/login" className="transition-colors hover:text-white" data-testid="footer-admin-link">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
